<template>
  <Teleport to="body" v-if="visible">
    <div class="modal-overlay" :style="maskStyle" @click="handleOverlayClick">
      <div class="modal-container" :class="modalClass" :style="getStyles" @click.stop>
        <!-- 头部 -->
        <div v-if="showHeader" class="modal-header" :class="{ 'no-border': !title.trim() }">
          <slot name="header" v-if="$slots.header"></slot>
          <h3 v-else-if="title" class="modal-title">
            {{ title }}
            <slot name="extra"></slot>
          </h3>
          <button
            v-if="showCloseButton"
            class="modal-close-btn"
            @click="handleClose"
            aria-label="关闭"
          >
            <IconClose />
          </button>
        </div>

        <!-- 内容区域 -->
        <div class="modal-content">
          <button
            v-if="showCloseButton && !showHeader"
            class="modal-close-btn modal-close-btn-only"
            @click="handleClose"
            aria-label="关闭"
          >
            <IconClose />
          </button>
          <slot></slot>
        </div>

        <!-- 底部按钮 -->
        <div v-if="$slots.footer" class="modal-footer">
          <slot name="footer"></slot>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import IconClose from '@rendora/design/assets/close.svg?component';
import { computed, onMounted, onUnmounted, provide, watch } from 'vue';
import { MODAL_LAYER_CONTEXT_KEY } from '../../constants/layer-context';
import { getDocument, safeRemoveBodyStyle, safeSetBodyStyle } from '../../utils/ssr';
import type { ModalProps } from './types';

interface Emits {
  (e: 'update:modelValue', value: boolean): void;
  (e: 'close'): void;
  (e: 'open'): void;
}

const props = withDefaults(defineProps<ModalProps>(), {
  modelValue: false,
  title: '',
  width: '520px',
  height: 'auto',
  maxHeight: '90vh',
  maxWidth: undefined,
  showHeader: true,
  showCloseButton: true,
  background: 'var(--x-white-1)',
  border: '1px solid var(--x-back-4)',
  borderRadius: '8px',
  closeOnOverlayClick: true,
  closeOnEsc: true,
  maskClosable: true,
  zIndex: 1000,
  centered: true,
  fullscreen: false,
  padding: 24,
  containerClass: '',
  boxShadow: 'var(--x-shadow)',
});

const emit = defineEmits<Emits>();

provide(MODAL_LAYER_CONTEXT_KEY, { zIndex: props.zIndex });

const visible = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
});

const modalClass = computed(() => [
  props.containerClass,
  {
    'modal-centered': props.centered,
    'modal-fullscreen': props.fullscreen,
  },
]);

const getStyles = computed(() => {
  if (props.minWidth) {
    return {
      height: props.height,
      maxHeight: props.maxHeight,
      width: props.width,
      minWidth: props.minWidth,
    };
  }
  return { height: props.height, maxHeight: props.maxHeight, width: props.width };
});

// 处理 ESC 键关闭
const handleEscKey = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && props.closeOnEsc && visible.value) {
    handleClose();
  }
};

// 处理遮罩层点击
const handleOverlayClick = () => {
  if (props.maskClosable && props.closeOnOverlayClick) {
    handleClose();
  }
};

// 关闭 modal
const handleClose = () => {
  visible.value = false;
  emit('close');
};

// 打开 modal
const handleOpen = () => {
  emit('open');
};

// 监听 visible 变化
watch(visible, newVal => {
  if (newVal) {
    handleOpen();
    // 禁止背景滚动（SSR 安全）
    safeSetBodyStyle('overflow', 'hidden');
  } else {
    // 恢复背景滚动（SSR 安全）
    safeRemoveBodyStyle('overflow');
  }
});

// 组件挂载时添加事件监听
onMounted(() => {
  if (props.closeOnEsc) {
    const doc = getDocument();
    if (doc) {
      doc.addEventListener('keydown', handleEscKey);
    }
  }
});

// 组件卸载时移除事件监听
onUnmounted(() => {
  const doc = getDocument();
  if (doc) {
    doc.removeEventListener('keydown', handleEscKey);
  }
  // 确保恢复背景滚动
  safeRemoveBodyStyle('overflow');
});
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: v-bind(zIndex);
  animation: fadeIn 0.2s ease-out;
}

.modal-container {
  background: v-bind(background);
  border-radius: v-bind(borderRadius);
  border: v-bind(border);
  box-shadow: v-bind(boxShadow);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: slideIn 0.2s ease-out;
}

.modal-centered {
  margin: auto;
}

.modal-fullscreen {
  width: 100vw !important;
  height: 100vh !important;
  max-width: 100vw;
  max-height: 100vh;
  border-radius: 0;
}

.modal-header {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  border-bottom: 1px solid var(--x-back-4);
  height: 48px;
}

.no-border {
  border-bottom: none;
}

.modal-title {
  margin: 0;
  font-size: 16px;
  font-weight: var(--x-weight-medium);
  color: var(--x-text-1);
  line-height: 22px;
  display: flex;
  align-items: center;
}

.modal-close-btn-only {
  position: absolute;
  top: 10px;
  right: 20px;
}

.modal-close-btn {
  z-index: var(--x-layer-absolute);
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: var(--x-radius-medium);
  height: 28px;
  width: 28px;
  color: var(--x-text-5);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.modal-close-btn:hover {
  background-color: var(--x-back-4);
}

.modal-content {
  position: relative;
  padding: v-bind(padding);
  min-height: 0;
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  min-height: 56px;
}

/* 动画效果 */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: scale(0.8) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* 响应式设计 */
@media (max-width: 768px) {
  .modal-container {
    /* width: 95vw !important; */
    margin: 20px;
  }

  /* .modal-header,
  .modal-content,
  .modal-footer {
    padding: 16px;
  } */
}
</style>
